export const ASSET_PREVIEW_STORAGE_KEY = "podscope.assetPreviewViewport.v1";
export const ASSET_PREVIEW_MIN_SCALE = 0.25;
export const ASSET_PREVIEW_MAX_SCALE = 1.25;
export const ASSET_PREVIEW_SCALE_STEP = 0.05;

export type AssetPreviewViewport = {
  scale: number;
  x: number;
  y: number;
};

export function clampAssetPreviewScale(scale: number): number {
  return Math.min(
    ASSET_PREVIEW_MAX_SCALE,
    Math.max(ASSET_PREVIEW_MIN_SCALE, scale),
  );
}

export function fitAssetPreviewScale(
  widthPx: number,
  heightPx: number,
  viewWidth: number,
  viewHeight: number,
  paddingPx = 48,
): number {
  if (widthPx <= 0 || heightPx <= 0 || viewWidth <= 0 || viewHeight <= 0) {
    return 0.55;
  }
  return clampAssetPreviewScale(
    Math.min(
      (viewWidth - paddingPx) / widthPx,
      (viewHeight - paddingPx) / heightPx,
    ),
  );
}

export function parseAssetPreviewViewport(
  raw: string | null,
): AssetPreviewViewport | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AssetPreviewViewport>;
    if (
      typeof parsed.scale !== "number" ||
      typeof parsed.x !== "number" ||
      typeof parsed.y !== "number" ||
      !Number.isFinite(parsed.scale) ||
      !Number.isFinite(parsed.x) ||
      !Number.isFinite(parsed.y)
    ) {
      return null;
    }
    return {
      scale: clampAssetPreviewScale(parsed.scale),
      x: parsed.x,
      y: parsed.y,
    };
  } catch {
    return null;
  }
}

export function loadAssetPreviewViewport(): AssetPreviewViewport | null {
  if (typeof window === "undefined") return null;
  return parseAssetPreviewViewport(
    window.localStorage.getItem(ASSET_PREVIEW_STORAGE_KEY),
  );
}

export function waitAssetPreviewPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

export function saveAssetPreviewViewport(viewport: AssetPreviewViewport): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    ASSET_PREVIEW_STORAGE_KEY,
    JSON.stringify({
      scale: clampAssetPreviewScale(viewport.scale),
      x: viewport.x,
      y: viewport.y,
    }),
  );
}
